import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Button, 
  Progress, 
  Typography, 
  Card, 
  Space, 
  Spin, 
  Alert,
  Radio,
  Input,
  Drawer,
  message
} from 'antd';
import { 
  CloseOutlined, 
  LeftOutlined, 
  RightOutlined,
  CheckOutlined,
  MenuOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined
} from '@ant-design/icons';
import { useLessonContents, useQuizQuestions, useQuestionAnswers, useSaveAnswerResult, useQuestionById } from '../hooks/lesson';
import { useResponsive } from '../hooks/useResponsive';
import authService from '../services/authService';
import { createUserProgress } from '../hooks/user';
import { useUserProgressWithCourseDetails } from '../hooks/user';
import { TopicProgress, LessonProgress } from '../types';
import { THEME_COLORS, GRADIENTS, COMMON_STYLES } from '../utils/theme';
import StudentWatermark from '../components/StudentWatermark';

const { Title, Text, Paragraph } = Typography;

const LessonViewer: React.FC = () => {
  const { lessonId, courseId } = useParams<{ lessonId: string; courseId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

  useEffect(() => {
    const updateFullscreen = () => {
      setIsVideoFullscreen(!!videoContainerRef.current && document.fullscreenElement === videoContainerRef.current);
    };
    document.addEventListener('fullscreenchange', updateFullscreen);
    return () => document.removeEventListener('fullscreenchange', updateFullscreen);
  }, []);

  const toggleVideoFullscreen = async () => {
    try {
      if (document.fullscreenElement === videoContainerRef.current) {
        await document.exitFullscreen();
      } else {
        // Fullscreen the wrapper so the watermark stays above the video.
        await videoContainerRef.current?.requestFullscreen();
      }
    } catch {
      message.error('Unable to change fullscreen mode. Please try again.');
    }
  };
  
  // Get lesson title from navigation state
  const lessonTitle = location.state?.lessonTitle;

  // Copy protection functions
  const preventCopy = (e: Event) => {
    e.preventDefault();
    return false;
  };

  const preventKeyboardShortcuts = (e: KeyboardEvent) => {
    // Prevent Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+S, Ctrl+P, F12, Ctrl+Shift+I, Ctrl+U
    if (e.ctrlKey && (e.key === 'a' || e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 's' || e.key === 'p' || e.key === 'u')) {
      e.preventDefault();
      return false;
    }
    // Prevent F12 (DevTools)
    if (e.key === 'F12') {
      e.preventDefault();
      return false;
    }
    // Prevent Ctrl+Shift+I (DevTools)
    if (e.ctrlKey && e.shiftKey && e.key === 'I') {
      e.preventDefault();
      return false;
    }
    // Prevent Ctrl+Shift+J (Console)
    if (e.ctrlKey && e.shiftKey && e.key === 'J') {
      e.preventDefault();
      return false;
    }
    // Prevent Ctrl+Shift+C (Element Inspector)
    if (e.ctrlKey && e.shiftKey && e.key === 'C') {
      e.preventDefault();
      return false;
    }
  };

  const preventRightClick = (e: MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const preventDragStart = (e: DragEvent) => {
    e.preventDefault();
    return false;
  };

  const preventSelectStart = (e: Event) => {
    e.preventDefault();
    return false;
  };

  // Add copy protection event listeners
  useEffect(() => {
    // Disable copy, cut, paste
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);
    document.addEventListener('paste', preventCopy);
    
    // Disable keyboard shortcuts
    document.addEventListener('keydown', preventKeyboardShortcuts);
    
    // Disable right-click context menu
    document.addEventListener('contextmenu', preventRightClick);
    
    // Disable text selection
    document.addEventListener('selectstart', preventSelectStart);
    
    // Disable drag and drop
    document.addEventListener('dragstart', preventDragStart);
    
    // Add CSS to disable text selection
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    (document.body.style as any).mozUserSelect = 'none';
    (document.body.style as any).msUserSelect = 'none';
    
    // Cleanup function
    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
      document.removeEventListener('paste', preventCopy);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
      document.removeEventListener('contextmenu', preventRightClick);
      document.removeEventListener('selectstart', preventSelectStart);
      document.removeEventListener('dragstart', preventDragStart);
      
      // Restore text selection
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      (document.body.style as any).mozUserSelect = '';
      (document.body.style as any).msUserSelect = '';
    };
  }, []);
  
  const { data: lessonContents, isLoading, error } = useLessonContents(undefined, lessonId);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [videoBlobUrl, setVideoBlobUrl] = useState<string>('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isCorrectAnswer, setIsCorrectAnswer] = useState<boolean | null>(null);
  const [selectedAnswerId, setSelectedAnswerId] = useState<string>('');
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [showLearningPath, setShowLearningPath] = useState(false);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);

  // Reset all state when lessonId changes (when navigating to a different lesson)
  useEffect(() => {
    setCurrentContentIndex(0);
    setCurrentQuestionIndex(0);
    setSelectedAnswer('');
    setIsAnswered(false);
    setIsCorrectAnswer(null);
    setSelectedAnswerId('');
    setVideoBlobUrl('');
    setIsVideoLoading(false);
    setIsImageLoading(false);
  }, [lessonId]);

  // Fetch user progress for learning path
  const { data: userProgress } = useUserProgressWithCourseDetails(courseId);
  const progressTopics: TopicProgress[] = userProgress || [];
  const currentTopic = progressTopics.length > 0 ? progressTopics[currentTopicIndex] : null;

  // Save answer result mutation
  const saveAnswerResult = useSaveAnswerResult();

  // Get current content
  const contents = Array.isArray(lessonContents) ? lessonContents : [lessonContents];
  const currentContent = contents[currentContentIndex];
  
  // Fetch quiz questions if current content is a quiz
  const quizId = currentContent?.content_type === 3 ? currentContent?.quiz_id || '1' : undefined;
  const { data: quizQuestions, isLoading: isQuizLoading } = useQuizQuestions(quizId);
  
  // Fetch question data if current content is a single question
  const singleQuestionId =  currentContent?.question_id ? currentContent?.question_id : undefined;
  const { data: singleQuestionData, isLoading: isSingleQuestionLoading } = useQuestionById(
    currentContent?.question_id != null ? singleQuestionId : undefined
  );

  // Fetch question answers for the current question
  const currentQuestionId = quizQuestions && quizQuestions.length > 0 ? quizQuestions[currentQuestionIndex]?.id : undefined;
  const { data: questionAnswers, isLoading: isAnswersLoading } = useQuestionAnswers(currentQuestionId?.toString());
  
  // Fetch answers for single question
  const singleQuestionAnswerId = currentContent?.content_type === 5 && singleQuestionData?.id ? singleQuestionData.id.toString() : undefined;
  const { data: singleQuestionAnswers, isLoading: isSingleQuestionAnswersLoading } = useQuestionAnswers(
    currentContent?.content_type === 5 ? singleQuestionAnswerId : undefined
  );

  // Create blob URL for video to hide source and handle loading states
  // useEffect(() => {
  //   const createVideoBlob = async () => {
  //     if (currentContent?.content && currentContent?.content_type === 2) {
  //       setIsVideoLoading(true);
  //       try {
  //         // Fetch video with credentials for authenticated requests
  //         const response = await fetch(currentContent.content);
          
  //         if (response.ok) {
  //           const blob = await response.blob();
  //           const blobUrl = URL.createObjectURL(blob);
  //           setVideoBlobUrl(blobUrl);
  //         } else {
  //           // If fetch fails, fallback to original URL
  //           setVideoBlobUrl(currentContent.content);
  //         }
  //       } catch (error) {
  //         console.error('Error creating video blob:', error);
  //         // Fallback to original URL if blob creation fails
  //         setVideoBlobUrl(currentContent.content);
  //       } finally {
  //         setIsVideoLoading(false);
  //       }
  //     } else {
  //       // Clear blob URL if not video content
  //       setVideoBlobUrl('');
  //       setIsVideoLoading(false);
  //     }
  //   };

  //   // Handle image loading state
  //   if (currentContent?.content && currentContent?.content_type === 4) {
  //     setIsImageLoading(true);
  //   } else {
  //     setIsImageLoading(false);
  //   }

  //   if (lessonContents) {
  //     createVideoBlob();
  //   }

  //   // Cleanup blob URL on unmount or content change
  //   return () => {
  //     if (videoBlobUrl) {
  //       URL.revokeObjectURL(videoBlobUrl);
  //     }
  //   };
  // }, [currentContent]);

  // Additional video protection
  const handleVideoContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const handleVideoLoad = () => {
    if (videoRef.current) {
      // Disable video downloading through additional attributes
      videoRef.current.setAttribute('controlsList', 'nodownload noremoteplayback nofullscreen');
      videoRef.current.setAttribute('disablePictureInPicture', 'true');
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !lessonContents) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f5f5f5',
        padding: '20px'
      }}>
        <Alert
          message="Error"
          description="Failed to load lesson content. Please try again later."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const totalContents = contents.length;
  const progressPercentage = totalContents > 0 ? ((currentContentIndex + 1) / totalContents) * 100 : 0;

  // Check content type for conditional rendering
  const contentType = currentContent?.content_type;
  const isText = contentType === 1;
  const isVideo = contentType === 2;
  const isQuiz = contentType === 3;
  const isImage = contentType === 4;
  const isSingleQuestion = contentType === 5;

  const handlePrevious = () => {
    if (currentContentIndex > 0) {
      setCurrentContentIndex(currentContentIndex - 1);
      setCurrentQuestionIndex(0); // Reset to first question for new content
      setSelectedAnswer('');
      setIsAnswered(false);
      setIsCorrectAnswer(null);
      setSelectedAnswerId('');
    }
  };

  const handleNext = () => {
    if (currentContentIndex < totalContents - 1) {
      setCurrentContentIndex(currentContentIndex + 1);
      setCurrentQuestionIndex(0); // Reset to first question for new content
      setSelectedAnswer('');
      setIsAnswered(false);
      setIsCorrectAnswer(null);
      setSelectedAnswerId('');
    }
  };

  const handleClose = () => {
    if (courseId) {
      navigate(`/course/${courseId}/learn`); // Go back to course learning page
    } else {
      navigate(-1); // Go back to previous page
    }
  };

  const handleAnswerSelect = (value: string, answerId: string, isCorrect: boolean) => {
    setSelectedAnswer(value);
    setSelectedAnswerId(answerId);
    setIsCorrectAnswer(isCorrect);
  };

  const handleComplete = async () => {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser || !lessonId) {
        console.error('Missing user or lesson information');
        return;
      }

      const progressData = {
        user_id: parseInt(currentUser.id || '7'),
        lesson_id: parseInt(lessonId),
        status: 1, // 1 for completed
        completion_date: new Date().toISOString(), // Format: 2025-09-29T11:42:00.410495Z
      };

      console.log('Creating user progress for lesson completion:', progressData);
      
      await createUserProgress(progressData);
      
      console.log('User progress created successfully');
      
      // Navigate back to course learning page
      navigate(`/course/${courseId}/learn`);
    } catch (error) {
      console.error('Error creating user progress:', error);
      // Still navigate even if progress creation fails
      navigate(`/course/${courseId}/learn`);
    }
  };

  const handleCheck = () => {
    // For quiz question with short answer (question_type = 3)
    if (contentType === 3 && quizQuestions && quizQuestions[currentQuestionIndex]?.question_type === 3 && questionAnswers && questionAnswers.length > 0) {
      const correctAnswer = questionAnswers[0]?.option_text || '';
      const userAnswer = selectedAnswer.trim().toLowerCase();
      const correctAnswerLower = correctAnswer.trim().toLowerCase();
      
      // Check if answer is correct (case-insensitive comparison)
      const isCorrect = userAnswer === correctAnswerLower;
      setIsCorrectAnswer(isCorrect);
      setIsAnswered(true);
      
      // Save answer result to backend
      const userId = authService.getCurrentUser()?.id || '1';
      if (quizQuestions[currentQuestionIndex]?.id) {
        saveAnswerResult.mutate({
          user_id: parseInt(userId, 10),
          question_id: quizQuestions[currentQuestionIndex].id,
          point: isCorrect ? 1 : 0,
          exam_id : 0
  
        });
      }
      return;
    }
    
    // For single question with short answer (question_type = 3)
    if (contentType === 5 && singleQuestionData?.question_type === 3 && singleQuestionAnswers && singleQuestionAnswers.length > 0) {
      const correctAnswer = singleQuestionAnswers[0]?.option_text || '';
      const userAnswer = selectedAnswer.trim().toLowerCase();
      const correctAnswerLower = correctAnswer.trim().toLowerCase();
      
      // Check if answer is correct (case-insensitive comparison)
      const isCorrect = userAnswer === correctAnswerLower;
      setIsCorrectAnswer(isCorrect);
      setIsAnswered(true);
      
      // Save answer result to backend
      const userId = authService.getCurrentUser()?.id || '1';
      if (singleQuestionData?.id) {
        saveAnswerResult.mutate({
          user_id: parseInt(userId, 10),
          question_id: singleQuestionData.id,
          point: isCorrect ? 1 : 0,
          exam_id : 0
        });
      }
      return;
    }
    
    setIsAnswered(true);
    
    // Save answer result to backend (if it's a quiz or single question)
    if ((contentType === 3) && isCorrectAnswer !== null) {
      // Get user ID from auth service or use a placeholder
      const userId = authService.getCurrentUser()?.id || '1'; // Adjust based on your auth implementation
      const questionId = contentType === 3 ? 
        quizQuestions[currentQuestionIndex]?.id : 
        singleQuestionData?.id;
      
      if (questionId) {
        saveAnswerResult.mutate({
          user_id: parseInt(userId, 10),
          question_id: questionId,
          point: isCorrectAnswer ? 1 : 0,
          exam_id : 0
        });
      }
    }
    
    // If content type is not quiz or single question, automatically go to next lesson
    if (contentType !== 3 && contentType !== 5) {
      // Small delay to show the check action, then navigate to next
      setTimeout(() => {
        if (currentContentIndex < totalContents - 1) {
          setCurrentContentIndex(currentContentIndex + 1);
          setSelectedAnswer('');
          setIsAnswered(false);
          setIsCorrectAnswer(null);
          setSelectedAnswerId('');
        }
      }, 500); // 500ms delay for smooth transition
    }
    // For quiz content (contentType === 3), just mark as answered and show feedback
  };

  // Handle moving to next question in quiz
  const handleNextQuestion = () => {
    if (isQuiz && quizQuestions && currentQuestionIndex < quizQuestions.length - 1) {
      // Move to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
      setIsAnswered(false);
      setIsCorrectAnswer(null);
      setSelectedAnswerId('');
    } else {
      // No more questions, move to next content
      handleNext();
    }
  };

  // Handle moving to previous question in quiz
  const handlePreviousQuestion = () => {
    if (isQuiz && currentQuestionIndex > 0) {
      // Move to previous question
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setSelectedAnswer('');
      setIsAnswered(false);
      setIsCorrectAnswer(null);
      setSelectedAnswerId('');
    } else {
      // At first question, move to previous content
      handlePrevious();
    }
  };

  // Function to render content_text when available
  const renderContentText = () => {
    if (!currentContent?.content_text) return null;
    
    return (
      <div style={{ 
        marginBottom: isMobile ? '20px' : '30px',
        padding: isMobile ? '16px' : '20px',
        background: '#f8f9fa',
        borderRadius: '12px',
        border: '1px solid #e9ecef'
      }}>
        <div 
          className="content-text"
          dangerouslySetInnerHTML={{ __html: currentContent.content_text }}
          style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontSize: isMobile ? '16px' : '18px',
            lineHeight: '1.7',
            color: '#2c3e50',
            letterSpacing: '0.3px',
            textAlign: 'justify'
          }}
        />
      </div>
    );
  };

  // Function to get button style based on answer state
  const getAnswerButtonStyle = (answer: any) => {
    const isSelected = selectedAnswer === answer.option_text;
    const baseStyle = {
      height: 'auto',
      padding: isMobile ? '16px 20px' : '20px 24px',
      borderRadius: '12px',
      textAlign: 'left' as const,
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
    };

    if (!isAnswered) {
      // Before answering - show selection state
      return {
        ...baseStyle,
        border: isSelected ? '2px solid #1890ff' : '2px solid #e8e8e8',
        background: isSelected ? '#f6ffed' : 'white',
        boxShadow: isSelected ? '0 2px 8px rgba(24, 144, 255, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
      };
    } else {
      // After answering - show correct/incorrect feedback
      if (answer.is_correct) {
        // Correct answer - green
        return {
          ...baseStyle,
          border: '2px solid #52c41a',
          background: '#f6ffed',
          boxShadow: '0 2px 8px rgba(82, 196, 26, 0.15)',
        };
      } else if (isSelected) {
        // Wrong selected answer - red
        return {
          ...baseStyle,
          border: '2px solid #ff4d4f',
          background: '#fff2f0',
          boxShadow: '0 2px 8px rgba(255, 77, 79, 0.15)',
        };
      } else {
        // Other unselected answers - gray
        return {
          ...baseStyle,
          border: '2px solid #e8e8e8',
          background: '#fafafa',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        };
      }
    }
  };

  // Helper function to check if lesson is completed
  const isLessonCompleted = (checkLessonId: string | number) => {
    for (const topic of progressTopics) {
      const lesson = topic.lessons.find(l => l.lesson_id.toString() === checkLessonId.toString());
      if (lesson) {
        return lesson.is_completed === 1;
      }
    }
    return false;
  };

  // Helper function to handle lesson navigation
  const handleLessonClick = (targetLessonId: string, lessonTitle?: string) => {
    navigate(`/course/${courseId}/lesson/${targetLessonId}`, {
      state: { lessonTitle: lessonTitle }
    });
    setShowLearningPath(false);
  };

  return (
    <div 
      className="lesson-viewer"
      style={{
        minHeight: '100vh',
        background: THEME_COLORS.gray3,
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent'
      }}
      onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
      onDragStart={(e: React.DragEvent) => e.preventDefault()}
      onDrop={(e: React.DragEvent) => e.preventDefault()}
      onDragOver={(e: React.DragEvent) => e.preventDefault()}
      onCopy={(e: React.ClipboardEvent) => e.preventDefault()}
      onCut={(e: React.ClipboardEvent) => e.preventDefault()}
      onPaste={(e: React.ClipboardEvent) => e.preventDefault()}
    >
      {/* Header with close button and progress bar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'white',
        padding: isMobile ? '12px 16px' : '16px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderBottom: `1px solid ${THEME_COLORS.gray2}`,
        zIndex: 1000,
        boxShadow: COMMON_STYLES.cardShadow
      }}>
        {/* Lesson Title Section */}
        {/* {lessonTitle && (
          <div style={{
            width: isMobile ? '100%' : '800px',
            marginBottom: '12px',
            textAlign: 'left'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div 
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: THEME_COLORS.primaryBlue,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Text style={{ color: THEME_COLORS.textLight, fontWeight: 600, fontSize: '16px' }}>2c479e</Text>
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  Quantitative Methods
                </Text>
                <Text strong style={{ fontSize: isMobile ? '14px' : '16px', display: 'block' }}>
                  {lessonTitle}
                </Text>
              </div>
              {currentContent?.duration && (
                <div style={{
                  backgroundColor: THEME_COLORS.gray3,
                  padding: '6px 12px',
                  borderRadius: '8px'
                }}>
                  <Text style={{ fontSize: '13px', color: THEME_COLORS.textSecondary }}>
                    {currentContent.duration} мин
                  </Text>
                </div>
              )}
            </div>
          </div>
        )} */}

        {/* Controls and Progress Bar */}
        <div style={{
          width: isMobile ? '100%' : '800px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <Button 
            type="text" 
            icon={<MenuOutlined />} 
            onClick={() => setShowLearningPath(true)}
            style={{ 
              fontSize: '18px',
              color: THEME_COLORS.textSecondary,
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          />
          
          <Button 
            type="text" 
            icon={<CloseOutlined />} 
            onClick={handleClose}
            style={{ 
              fontSize: '18px',
              color: THEME_COLORS.textSecondary,
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          />
          
          <div style={{ flex: 1 }}>
            <Progress 
              percent={progressPercentage} 
              showInfo={false}
              strokeColor={THEME_COLORS.primaryBlue}
              trailColor={THEME_COLORS.gray2}
              strokeWidth={10}
              style={{ margin: 0 , borderRadius: '8px' }}
            />
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '20px 20px 80px' : '40px 60px 100px',
        paddingTop: isMobile ? '140px' : '180px',
        minHeight: '100vh',
        boxSizing: 'border-box'
      }}>
        <div style={{
          width: isMobile ? '100%' : '800px',
          margin: '0 auto'
        }}>
          <div style={{ 
            padding: 0,
            textAlign: 'left',
            minHeight: isMobile ? '200px' : '300px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start'
          }}>
            {/* Content based on content_type */}
            {isQuiz ? (
              // Quiz content (content_type = 3)
              <div>
                {isQuizLoading || isAnswersLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                  </div>
                ) : quizQuestions && quizQuestions.length > 0 ? (
                  <>
                    <Title level={2} style={{ 
                      marginBottom: '30px',
                      fontSize: isMobile ? '20px' : '32px',
                      color: '#1f1f1f',
                      lineHeight: '1.3',
                      fontWeight: '700',
                      textAlign: 'left'
                    }}>
                      {'Quiz Question ' + (currentQuestionIndex + 1) + ' of ' + quizQuestions.length}
                    </Title>
                    
                    {quizQuestions[currentQuestionIndex]?.question && (
                      <div 
                        dangerouslySetInnerHTML={{ __html: quizQuestions[currentQuestionIndex].question }}
                        style={{ 
                          fontSize: isMobile ? '16px' : '18px',
                          color: '#333',
                          marginBottom: isMobile ? '20px' : '30px',
                          lineHeight: '1.6',
                          textAlign: 'left',
                          fontWeight: '600'
                        }}
                      />
                    )}
                    
                    {quizQuestions[currentQuestionIndex]?.text && (
                      <div 
                        dangerouslySetInnerHTML={{ __html: quizQuestions[currentQuestionIndex].text }}
                        style={{ 
                          fontSize: isMobile ? '16px' : '18px',
                          color: '#333',
                          marginBottom: isMobile ? '30px' : '40px',
                          lineHeight: '1.6',
                          textAlign: 'left'
                        }}
                      />
                    )}
                    {quizQuestions[currentQuestionIndex]?.image && (
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: isMobile ? '20px' : '30px'
                      }}>
                        <img 
                          src={quizQuestions[currentQuestionIndex]?.image}
                          alt="Question image"
                          style={{ 
                            maxWidth: '100%', 
                            height: 'auto',
                            borderRadius: '12px',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                            objectFit: 'contain',
                            maxHeight: isMobile ? '250px' : '400px'
                          }}
                          onError={(e) => {
                            console.error('Error loading question image:', e);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    {/* Render content_text if available */}
                    {renderContentText()}
                    
                    {/* Answer Options */}
                    {quizQuestions[currentQuestionIndex]?.question_type === 3 ? (
                      // Short Answer Question - Show Input Field
                      <div style={{ width: '100%' }}>
                        <Input.TextArea
                          rows={4}
                          placeholder="Type your answer here..."
                          value={selectedAnswer}
                          onChange={(e) => setSelectedAnswer(e.target.value)}
                          disabled={isAnswered}
                          style={{
                            fontSize: isMobile ? '16px' : '18px',
                            padding: isMobile ? '12px 16px' : '16px 20px',
                            borderRadius: '12px',
                            border: isAnswered 
                              ? (isCorrectAnswer ? '2px solid #52c41a' : '2px solid #ff4d4f')
                              : '2px solid #e8e8e8',
                            background: isAnswered 
                              ? (isCorrectAnswer ? '#f6ffed' : '#fff2f0')
                              : 'white',
                            boxShadow: isAnswered 
                              ? (isCorrectAnswer ? '0 2px 8px rgba(82, 196, 26, 0.15)' : '0 2px 8px rgba(255, 77, 79, 0.15)')
                              : '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.2s ease'
                          }}
                        />
                        {isAnswered && questionAnswers && questionAnswers.length > 0 && (
                          <div style={{
                            marginTop: '16px',
                            padding: isMobile ? '12px 16px' : '16px 20px',
                            background: '#f6ffed',
                            border: '2px solid #52c41a',
                            borderRadius: '12px'
                          }}>
                            <Text strong style={{ color: '#52c41a', fontSize: isMobile ? '14px' : '16px' }}>
                              <CheckOutlined style={{ marginRight: '8px' }} />
                              Correct Answer:
                            </Text>
                            <br />
                            <Text style={{ fontSize: isMobile ? '16px' : '18px', color: '#2c3e50', marginTop: '8px', display: 'block' }}>
                              {questionAnswers[0]?.option_text || ''}
                            </Text>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Multiple Choice Question - Show Answer Buttons
                      <Space direction="vertical" size={isMobile ? "middle" : "large"} style={{ width: '100%' }}>
                        {questionAnswers && questionAnswers.length > 0 ? (
                          questionAnswers.map((answer: any, index: number) => (
                            <Button
                              key={index}
                              size="large"
                              block
                              disabled={isAnswered}
                              style={getAnswerButtonStyle(answer)}
                              onClick={() => handleAnswerSelect(answer.option_text, answer.id.toString(), answer.is_correct)}
                              onMouseEnter={(e) => {
                                if (!isAnswered && selectedAnswer !== answer.option_text) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isAnswered && selectedAnswer !== answer.option_text) {
                                  e.currentTarget.style.borderColor = '#e8e8e8';
                                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                                }
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Text style={{ 
                                  fontSize: isMobile ? '16px' : '18px', 
                                  color: '#1f1f1f',
                                  flex: 1
                                }}>
                                  {answer.option_text || ''}
                                </Text>
                                {isAnswered && answer.is_correct === true && (
                                  <CheckOutlined style={{ 
                                    color: '#52c41a', 
                                    fontSize: '20px',
                                    marginLeft: '12px'
                                  }} />
                                )}
                              </div>
                            </Button>
                          ))
                        ) : (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Text>No answer options available</Text>
                          </div>
                        )}
                      </Space>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text>No quiz questions available</Text>
                  </div>
                )}
              </div>
            ) : isSingleQuestion ? (
              // Single Question content (content_type = 5)
              <div>
                {isSingleQuestionLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                  </div>
                ) : singleQuestionData ? (
                  <>
                    <Title level={2} style={{ 
                      marginBottom: '30px',
                      fontSize: isMobile ? '20px' : '32px',
                      color: '#1f1f1f',
                      lineHeight: '1.3',
                      fontWeight: '700',
                      textAlign: 'left'
                    }}>
                      Question
                    </Title>
                    
                    {singleQuestionData.question && (
                      <div 
                        dangerouslySetInnerHTML={{ __html: singleQuestionData.question }}
                        style={{ 
                          fontSize: isMobile ? '16px' : '18px',
                          color: '#333',
                          marginBottom: isMobile ? '20px' : '30px',
                          lineHeight: '1.6',
                          textAlign: 'left',
                          fontWeight: '600'
                        }}
                      />
                    )}

                    
                    
                    {singleQuestionData.text && (
                      <div 
                        dangerouslySetInnerHTML={{ __html: singleQuestionData.text }}
                        style={{ 
                          fontSize: isMobile ? '16px' : '18px',
                          color: '#333',
                          marginBottom: isMobile ? '30px' : '40px',
                          lineHeight: '1.6',
                          textAlign: 'left'
                        }}
                      />
                    )}

                    {/* Image display below question if available */}
                    {singleQuestionData.image && (
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: isMobile ? '20px' : '30px'
                      }}>
                        <img 
                          src={singleQuestionData.image}
                          alt="Question image"
                          style={{ 
                            maxWidth: '100%', 
                            height: 'auto',
                            borderRadius: '12px',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                            objectFit: 'contain',
                            maxHeight: isMobile ? '250px' : '400px'
                          }}
                          onError={(e) => {
                            console.error('Error loading question image:', e);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Render content_text if available */}
                    {renderContentText()}

                    {/* Answer Options for Single Question */}
                    {singleQuestionData.question_type === 3 ? (
                      // Short Answer Question - Show Input Field
                      <div style={{ width: '100%' }}>
                        <Input.TextArea
                          rows={4}
                          placeholder="Type your answer here..."
                          value={selectedAnswer}
                          onChange={(e) => setSelectedAnswer(e.target.value)}
                          style={{
                            fontSize: isMobile ? '16px' : '18px',
                            padding: isMobile ? '12px 16px' : '16px 20px',
                            borderRadius: '12px',
                            border: isAnswered 
                              ? (isCorrectAnswer ? '2px solid #52c41a' : '2px solid #ff4d4f')
                              : '2px solid #e8e8e8',
                            background: isAnswered 
                              ? (isCorrectAnswer ? '#f6ffed' : '#fff2f0')
                              : 'white',
                            boxShadow: isAnswered 
                              ? (isCorrectAnswer ? '0 2px 8px rgba(82, 196, 26, 0.15)' : '0 2px 8px rgba(255, 77, 79, 0.15)')
                              : '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.2s ease'
                          }}
                        />
                        {isAnswered && singleQuestionAnswers && singleQuestionAnswers.length > 0 && (
                          <div style={{
                            marginTop: '16px',
                            padding: isMobile ? '12px 16px' : '16px 20px',
                            background: '#f6ffed',
                            border: '2px solid #52c41a',
                            borderRadius: '12px'
                          }}>
                            <Text strong style={{ color: '#52c41a', fontSize: isMobile ? '14px' : '16px' }}>
                              <CheckOutlined style={{ marginRight: '8px' }} />
                              Correct Answer:
                            </Text>
                            <br />
                            <Text style={{ fontSize: isMobile ? '16px' : '18px', color: '#2c3e50', marginTop: '8px', display: 'block' }}>
                              {singleQuestionAnswers[0]?.option_text || ''}
                            </Text>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Multiple Choice Question - Show Answer Buttons
                      <Space direction="vertical" size={isMobile ? "middle" : "large"} style={{ width: '100%' }}>
                        {isSingleQuestionAnswersLoading ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Spin />
                          </div>
                        ) : singleQuestionAnswers && singleQuestionAnswers.length > 0 ? (
                          singleQuestionAnswers.map((answer: any, index: number) => (
                            <Button
                              key={index}
                              size="large"
                              block
                              disabled={isAnswered}
                              style={getAnswerButtonStyle(answer)}
                              onClick={() => handleAnswerSelect(answer.option_text, answer.id.toString(), answer.is_correct)}
                              onMouseEnter={(e) => {
                                if (!isAnswered && selectedAnswer !== answer.option_text) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isAnswered && selectedAnswer !== answer.option_text) {
                                  e.currentTarget.style.borderColor = '#e8e8e8';
                                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                                }
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Text style={{ 
                                  fontSize: isMobile ? '16px' : '18px', 
                                  color: '#1f1f1f',
                                  flex: 1
                                }}>
                                  {answer.option_text || ''}
                                </Text>
                                {isAnswered && answer.is_correct === true && (
                                  <CheckOutlined style={{ 
                                    color: '#52c41a', 
                                    fontSize: '20px',
                                    marginLeft: '12px'
                                  }} />
                                )}
                              </div>
                            </Button>
                          ))
                        ) : (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Text>No answer options available</Text>
                          </div>
                        )}
                      </Space>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text>Question not found</Text>
                  </div>
                )}
              </div>
            ) : isVideo ? (
              // Video content (content_type = 2)
              <div>
                <Title level={2} style={{ 
                  marginBottom: '30px',
                  fontSize: isMobile ? '20px' : '32px',
                  color: '#1f1f1f',
                  lineHeight: '1.3',
                  fontWeight: '700',
                  textAlign: 'left'
                }}>
                  {currentContent.title || 'Video Lesson'}
                </Title>
                
                {/* Video player */}
                <div ref={videoContainerRef} style={{
                  width: '100%',
                  height: isVideoFullscreen ? '100%' : isMobile ? '250px' : '400px',
                  backgroundColor: '#000',
                  borderRadius: isVideoFullscreen ? 0 : '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: isVideoFullscreen ? 0 : '30px',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                }}>
                  <StudentWatermark
                    style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {isVideoLoading ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <Spin size="large" />
                        <Text style={{ color: '#999', fontSize: '16px', textAlign: 'center' }}>
                          Loading video...
                        </Text>
                      </div>
                    ) : currentContent?.content ? (
                      <video
                        ref={videoRef}
                        controls
                        controlsList="nodownload noremoteplayback nofullscreen"
                        disablePictureInPicture
                        disableRemotePlayback
                        playsInline
                        onContextMenu={handleVideoContextMenu}
                        onLoadedData={handleVideoLoad}
                        onDragStart={(e) => e.preventDefault()}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '16px',
                          objectFit: 'contain',
                          outline: 'none',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none'
                        }}
                        src={currentContent.content}
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 16px rgba(82, 196, 26, 0.3)'
                        }}>
                          <div style={{
                            width: 0,
                            height: 0,
                            borderLeft: '20px solid white',
                            borderTop: '12px solid transparent',
                            borderBottom: '12px solid transparent',
                            marginLeft: '4px'
                          }} />
                        </div>
                        <Text style={{ color: '#999', fontSize: '18px', textAlign: 'center' }}>
                          Video content will be displayed here
                        </Text>
                      </div>
                    )}
                  </StudentWatermark>
                  {currentContent?.content && !isVideoLoading && document.fullscreenEnabled && (
                    <Button
                      icon={isVideoFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                      aria-label={isVideoFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                      title={isVideoFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                      onClick={toggleVideoFullscreen}
                      style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}
                    />
                  )}
                </div>
                
                {/* Render content_text if available */}
                {renderContentText()}
                
                {currentContent?.description && (
                  <Paragraph style={{ 
                    fontSize: isMobile ? '16px' : '18px',
                    color: '#333',
                    lineHeight: '1.6',
                    textAlign: 'left'
                  }}>
                    {currentContent.description}
                  </Paragraph>
                )}
              </div>
            ) : isImage ? (
              // Image content (content_type = 4)
              <div>
                <Title level={2} style={{ 
                  marginBottom: '30px',
                  fontSize: isMobile ? '20px' : '32px',
                  color: '#1f1f1f',
                  lineHeight: '1.3',
                  fontWeight: '700',
                  textAlign: 'left'
                }}>
                  {currentContent.title || lessonTitle || 'Image Lesson'}
                </Title>
                
                {/* Image display */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '30px'
                }}>
                  {currentContent?.content ? (
                    <img 
                      src={currentContent.content}
                      alt={lessonTitle || 'Lesson image'}
                      style={{ 
                        maxWidth: '100%', 
                        height: 'auto',
                        borderRadius: '12px',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                        objectFit: 'contain',
                        maxHeight: isMobile ? '300px' : '500px'
                      }}
                      onError={(e) => {
                        console.error('Error loading image:', e);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: isMobile ? '200px' : '300px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #d9d9d9'
                    }}>
                      <Text style={{ color: '#999', fontSize: '18px', textAlign: 'center' }}>
                        No image available
                      </Text>
                    </div>
                  )}
                </div>
                
                {currentContent?.description && (
                  <Paragraph style={{ 
                    fontSize: isMobile ? '16px' : '18px',
                    color: '#333',
                    lineHeight: '1.6',
                    textAlign: 'left'
                  }}>
                    {currentContent.description}
                  </Paragraph>
                )}
              </div>
            ) : (
              // Text content (content_type = 1) or default
              <div>
                <Title level={2} style={{ 
                  marginBottom: '30px',
                  fontSize: isMobile ? '20px' : '32px',
                  color: '#1f1f1f',
                  lineHeight: '1.3',
                  fontWeight: '700',
                  textAlign: 'left'
                }}>
                  {currentContent?.title || lessonTitle || 'Text Content'}
                </Title>
                
                <div 
                  style={{ 
                    fontSize: isMobile ? '16px' : '18px',
                    color: '#333',
                    lineHeight: '1.6',
                    textAlign: 'justify'
                  }}
                >
                  {currentContent?.content ? (
                    <div 
                      className="lesson-content"
                      dangerouslySetInnerHTML={{ __html: currentContent.content }}
                      style={{
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        fontSize: isMobile ? '16px' : '18px',
                        lineHeight: '1.7',
                        color: '#2c3e50',
                        letterSpacing: '0.3px',
                        textAlign: 'justify'
                      }}
                    />
                  ) : (
                    <Paragraph style={{ 
                      fontSize: isMobile ? '16px' : '18px',
                      color: '#333',
                      lineHeight: '1.6',
                      margin: 0
                    }}>
                      {currentContent?.description || 'Content will be displayed here.'}
                    </Paragraph>
                  )}
                </div>
                
                {/* Add global CSS styles for parsed HTML content */}
                <style dangerouslySetInnerHTML={{
                  __html: `
                    .lesson-content h1, .lesson-content h2, .lesson-content h3, 
                    .lesson-content h4, .lesson-content h5, .lesson-content h6 {
                      font-weight: 700 !important;
                      margin: ${isMobile ? '20px' : '24px'} 0 ${isMobile ? '12px' : '16px'} 0 !important;
                      color: #1f1f1f !important;
                      line-height: 1.3 !important;
                    }
                    
                    .lesson-content h1 { font-size: ${isMobile ? '24px' : '32px'} !important; }
                    .lesson-content h2 { font-size: ${isMobile ? '20px' : '28px'} !important; }
                    .lesson-content h3 { font-size: ${isMobile ? '18px' : '24px'} !important; }
                    .lesson-content h4 { font-size: ${isMobile ? '16px' : '20px'} !important; }
                    .lesson-content h5 { font-size: ${isMobile ? '15px' : '18px'} !important; }
                    .lesson-content h6 { font-size: ${isMobile ? '14px' : '16px'} !important; }
                    
                    .lesson-content p {
                      margin: ${isMobile ? '12px' : '16px'} 0 !important;
                      line-height: 1.7 !important;
                      color: #2c3e50 !important;
                      text-align: justify !important;
                      text-justify: inter-word !important;
                    }
                    
                    .lesson-content ul, .lesson-content ol {
                      margin: ${isMobile ? '12px' : '16px'} 0 !important;
                      padding-left: ${isMobile ? '20px' : '24px'} !important;
                    }
                    
                    .lesson-content li {
                      margin: ${isMobile ? '6px' : '8px'} 0 !important;
                      line-height: 1.6 !important;
                      color: #2c3e50 !important;
                      text-align: justify !important;
                      text-justify: inter-word !important;
                    }
                    
                    .lesson-content strong, .lesson-content b {
                      font-weight: 600 !important;
                      color: #1f1f1f !important;
                    }
                    
                    .lesson-content em, .lesson-content i {
                      font-style: italic !important;
                      color: #34495e !important;
                    }
                    
                    .lesson-content code {
                      background: #f8f9fa !important;
                      border: 1px solid #e9ecef !important;
                      border-radius: 4px !important;
                      padding: 2px 6px !important;
                      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
                      font-size: ${isMobile ? '14px' : '16px'} !important;
                      color: #e83e8c !important;
                    }
                    
                    .lesson-content pre {
                      background: #f8f9fa !important;
                      border: 1px solid #e9ecef !important;
                      border-radius: 8px !important;
                      padding: ${isMobile ? '12px' : '16px'} !important;
                      margin: ${isMobile ? '16px' : '20px'} 0 !important;
                      overflow-x: auto !important;
                      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
                      font-size: ${isMobile ? '14px' : '15px'} !important;
                      line-height: 1.5 !important;
                    }
                    
                    .lesson-content blockquote {
                      border-left: 4px solid #52c41a !important;
                      background: #f6ffed !important;
                      margin: ${isMobile ? '16px' : '20px'} 0 !important;
                      padding: ${isMobile ? '12px 16px' : '16px 20px'} !important;
                      border-radius: 0 8px 8px 0 !important;
                      font-style: italic !important;
                      color: #2c3e50 !important;
                      text-align: justify !important;
                      text-justify: inter-word !important;
                    }
                    
                    .lesson-content table {
                      width: 100% !important;
                      border-collapse: collapse !important;
                      margin: ${isMobile ? '16px' : '20px'} 0 !important;
                      background: white !important;
                      border-radius: 8px !important;
                      overflow: hidden !important;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
                    }
                    
                    .lesson-content th, .lesson-content td {
                      padding: ${isMobile ? '8px 12px' : '12px 16px'} !important;
                      text-align: left !important;
                      border-bottom: 1px solid #f0f0f0 !important;
                    }
                    
                    .lesson-content th {
                      background: #fafafa !important;
                      font-weight: 600 !important;
                      color: #1f1f1f !important;
                    }
                    
                    .lesson-content a {
                      color: #1890ff !important;
                      text-decoration: none !important;
                      border-bottom: 1px solid transparent !important;
                      transition: all 0.2s ease !important;
                    }
                    
                    .lesson-content a:hover {
                      color: #40a9ff !important;
                      border-bottom-color: #40a9ff !important;
                    }
                    
                    .lesson-content img {
                      max-width: 100% !important;
                      height: auto !important;
                      border-radius: 8px !important;
                      margin: ${isMobile ? '12px' : '16px'} 0 !important;
                      box-shadow: 0 2px 12px rgba(0,0,0,0.1) !important;
                    }
                    
                    .lesson-content hr {
                      border: none !important;
                      border-top: 2px solid #f0f0f0 !important;
                      margin: ${isMobile ? '24px' : '32px'} 0 !important;
                    }
                  `
                }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quiz/Question Feedback Section */}
      {(isQuiz || isSingleQuestion) && isAnswered && (
        <div style={{
          position: 'fixed',
          bottom: '80px',
          left: 0,
          right: 0,
          background: isCorrectAnswer ? '#52c41a' : '#ff4d4f',
          padding: isMobile ? '16px' : '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: isMobile ? '16px' : '18px',
          zIndex: 999,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isCorrectAnswer ? (
              <>
                <CheckOutlined style={{ fontSize: '24px' }} />
                <span>Correct!</span>
                {/* <span style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  padding: '4px 12px', 
                  borderRadius: '20px',
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  +15 XP
                </span> */}
              </>
            ) : (
              <>
                <span>❌</span>
                <span>Incorrect. Try again!</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        padding: isMobile ? '12px 16px' : '16px 24px',
        borderTop: `1px solid ${THEME_COLORS.gray2}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000,
        boxShadow: COMMON_STYLES.cardShadow
      }}>
        <Button 
          type="text" 
          icon={<LeftOutlined />}
          onClick={isQuiz ? handlePreviousQuestion : handlePrevious}
          disabled={currentContentIndex === 0 && (!isQuiz || currentQuestionIndex === 0)}
          style={{ 
            fontSize: isMobile ? '16px' : '18px',
            opacity: (currentContentIndex === 0 && (!isQuiz || currentQuestionIndex === 0)) ? 0.3 : 1,
            width: isMobile ? '48px' : '56px',
            height: isMobile ? '48px' : '56px',
            borderRadius: '50%',
            background: (currentContentIndex === 0 && (!isQuiz || currentQuestionIndex === 0)) ? THEME_COLORS.gray3 : 'white',
            border: `1px solid ${(currentContentIndex === 0 && (!isQuiz || currentQuestionIndex === 0)) ? THEME_COLORS.gray2 : THEME_COLORS.gray1}`,
            boxShadow: (currentContentIndex === 0 && (!isQuiz || currentQuestionIndex === 0)) ? 'none' : COMMON_STYLES.cardShadow,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (currentContentIndex === 0 && (!isQuiz || currentQuestionIndex === 0)) ? THEME_COLORS.gray1 : THEME_COLORS.textSecondary
          }}
          onMouseEnter={(e) => {
            if (!(currentContentIndex === 0 && (!isQuiz || currentQuestionIndex === 0))) {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              e.currentTarget.style.background = THEME_COLORS.gray4;
            }
          }}
          onMouseLeave={(e) => {
            if (!(currentContentIndex === 0 && (!isQuiz || currentQuestionIndex === 0))) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = COMMON_STYLES.cardShadow;
              e.currentTarget.style.background = 'white';
            }
          }}
        />

        {/* Center content - progress indicator and buttons */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: isMobile ? '8px' : '16px'
        }}>
          {/* Check button logic: show for quiz/single question when answer selected, always show for other content types */}
          {(((isQuiz || isSingleQuestion) && !isAnswered && selectedAnswer) || (!isQuiz && !isSingleQuestion && !isAnswered)) && (
            <Button 
              type="primary"
              onClick={handleCheck}
              size="large"
              style={{
                borderRadius: isMobile ? '24px' : '32px',
                padding: isMobile ? '16px 32px' : '16px 48px',
                height: isMobile ? '56px' : '56px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '600',
                background: THEME_COLORS.primaryBlue,
                borderColor: THEME_COLORS.primaryBlue,
                boxShadow: '0 4px 16px rgba(44, 71, 158, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: isMobile ? '120px' : '140px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(44, 71, 158, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(44, 71, 158, 0.3)';
              }}
            >
              <CheckOutlined style={{ marginRight: '8px', fontSize: isMobile ? '16px' : '18px' }} />
              Check
            </Button>
          )}

          {(((isQuiz || isSingleQuestion) && isAnswered) || (!isQuiz && !isSingleQuestion && isAnswered)) && (
            <>
              {isQuiz ? (
                // Quiz specific buttons
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
                  {/* <Button 
                    style={{
                      borderRadius: '20px',
                      padding: isMobile ? '8px 16px' : '10px 20px',
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: '500',
                      background: '#f5f5f5',
                      border: '1px solid #d9d9d9',
                      color: '#666'
                    }}
                  >
                    Why?
                  </Button> */}
                    {(currentContentIndex === totalContents - 1 && 
                      (!quizQuestions || currentQuestionIndex === quizQuestions.length - 1)) ? (
                      <Button style={{ 
                        color: '#52c41a', 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '12px' : '14px',
                        marginLeft: isMobile ? '4px' : '8px',
                      }}
                        onClick={handleComplete}
                      >
                        Complete!
                      </Button>
                    ) : (
                      <Button 
                        type="primary"
                        onClick={handleNextQuestion}
                        style={{
                          borderRadius: '20px',
                          padding: isMobile ? '8px 24px' : '10px 32px',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: '600',
                          background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                          border: 'none',
                          minWidth: isMobile ? '100px' : '120px'
                        }}
                      >
                        {quizQuestions && currentQuestionIndex < quizQuestions.length - 1 ? 'Next Question' : 'Continue'}
                      </Button>
                    )}                
                </div>
              ) : isSingleQuestion ? (
                // Single Question specific buttons
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
                  {currentContentIndex === totalContents - 1 ? (
                    <Button style={{ 
                      color: '#52c41a', 
                      fontWeight: 'bold',
                      fontSize: isMobile ? '12px' : '14px',
                      marginLeft: isMobile ? '4px' : '8px',
                    }}
                      onClick={handleComplete}
                    >
                      Complete!
                    </Button>
                  ) : (
                    <Button 
                      type="primary"
                      onClick={handleNext}
                      style={{
                        borderRadius: '20px',
                        padding: isMobile ? '8px 24px' : '10px 32px',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                        border: 'none',
                        minWidth: isMobile ? '100px' : '120px'
                      }}
                    >
                      Continue
                    </Button>
                  )}
                </div>
              ) : (
                // Non-quiz content
                <>
                  <Text style={{ 
                    color: '#999', 
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 'normal'
                  }}>
                    {currentContentIndex + 1} of {totalContents}
                  </Text>
                  {currentContentIndex === totalContents - 1 && (
                    <Button style={{ 
                      color: '#52c41a', 
                      fontWeight: 'bold',
                      fontSize: isMobile ? '12px' : '14px',
                      marginLeft: isMobile ? '4px' : '8px',
                    }}
                      onClick={handleComplete}
                    >
                      Complete!
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <Button 
          type="text" 
          icon={<RightOutlined />}
          onClick={isQuiz ? handleNextQuestion : handleNext}
          disabled={currentContentIndex === totalContents - 1 && (!isQuiz || (quizQuestions && currentQuestionIndex === quizQuestions.length - 1))}
          style={{ 
            fontSize: isMobile ? '16px' : '18px',
            opacity: (currentContentIndex === totalContents - 1 && (!isQuiz || (quizQuestions && currentQuestionIndex === quizQuestions.length - 1))) ? 0.3 : 1,
            width: isMobile ? '48px' : '56px',
            height: isMobile ? '48px' : '56px',
            borderRadius: '50%',
            background: (currentContentIndex === totalContents - 1 && (!isQuiz || (quizQuestions && currentQuestionIndex === quizQuestions.length - 1))) ? THEME_COLORS.gray3 : 'white',
            border: `1px solid ${(currentContentIndex === totalContents - 1 && (!isQuiz || (quizQuestions && currentQuestionIndex === quizQuestions.length - 1))) ? THEME_COLORS.gray2 : THEME_COLORS.gray1}`,
            boxShadow: (currentContentIndex === totalContents - 1 && (!isQuiz || (quizQuestions && currentQuestionIndex === quizQuestions.length - 1))) ? 'none' : COMMON_STYLES.cardShadow,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (currentContentIndex === totalContents - 1 && (!isQuiz || (quizQuestions && currentQuestionIndex === quizQuestions.length - 1))) ? THEME_COLORS.gray1 : THEME_COLORS.textSecondary
          }}
          onMouseEnter={(e) => {
            if (!(currentContentIndex === totalContents - 1 && (!isQuiz || (quizQuestions && currentQuestionIndex === quizQuestions.length - 1)))) {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              e.currentTarget.style.background = THEME_COLORS.gray4;
            }
          }}
          onMouseLeave={(e) => {
            if (!(currentContentIndex === totalContents - 1 && (!isQuiz || (quizQuestions && currentQuestionIndex === quizQuestions.length - 1)))) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = COMMON_STYLES.cardShadow;
              e.currentTarget.style.background = 'white';
            }
          }}
        />
      </div>

      {/* Copy Protection CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .lesson-viewer * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }
        
        .lesson-viewer *::selection {
          background: transparent !important;
        }
        
        .lesson-viewer *::-moz-selection {
          background: transparent !important;
        }
        
        .lesson-viewer img {
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
          pointer-events: none !important;
        }
        
        .lesson-viewer video {
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
        }
        
        .lesson-viewer {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        
        /* Question HTML Content Styles */
        .lesson-viewer table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: ${isMobile ? '16px' : '20px'} 0 !important;
          background: white !important;
          border: 1px solid #e0e0e0 !important;
          border-radius: 8px !important;
          overflow: hidden !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
        }
        
        .lesson-viewer th, .lesson-viewer td {
          padding: ${isMobile ? '8px 12px' : '10px 14px'} !important;
          text-align: left !important;
          border: 1px solid #e0e0e0 !important;
          font-size: ${isMobile ? '13px' : '14px'} !important;
          color: #333 !important;
        }
        
        .lesson-viewer th {
          background: #fafafa !important;
          font-weight: 600 !important;
          color: #1f1f1f !important;
        }
        
        .lesson-viewer tbody tr:hover {
          background: #f5f5f5 !important;
        }
        
        .lesson-viewer colgroup col {
          border: none !important;
        }
        
        .lesson-viewer strong, .lesson-viewer b {
          font-weight: 600 !important;
          color: #1f1f1f !important;
        }
        
        .lesson-viewer p {
          margin: ${isMobile ? '8px' : '12px'} 0 !important;
          line-height: 1.6 !important;
          color: #333 !important;
        }
        
        .lesson-viewer ul, .lesson-viewer ol {
          margin: ${isMobile ? '12px' : '16px'} 0 !important;
          padding-left: ${isMobile ? '20px' : '24px'} !important;
        }
        
        .lesson-viewer li {
          margin: ${isMobile ? '6px' : '8px'} 0 !important;
          line-height: 1.6 !important;
          color: #333 !important;
        }
        `
      }} />

      {/* Learning Path Drawer */}
      <Drawer
        title="Learning Path"
        placement="left"
        onClose={() => setShowLearningPath(false)}
        open={showLearningPath}
        width={isMobile ? '85%' : 400}
        styles={{
          body: { padding: isMobile ? '16px' : '24px', background: '#f8f9fa' }
        }}
      >
        {progressTopics.length > 0 ? (
          <>
            {/* Topic Navigation */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px',
              padding: '12px 16px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              {/* Left Arrow Button */}
              <Button
                type="text"
                onClick={() => {
                  if (currentTopicIndex > 0) {
                    setCurrentTopicIndex(currentTopicIndex - 1);
                  }
                }}
                disabled={!currentTopic || currentTopicIndex === 0}
                style={{
                  border: 'none',
                  background: 'white',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: (!currentTopic || currentTopicIndex === 0) ? 0.5 : 1
                }}
              >
                <ArrowLeftOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />
              </Button>

              {/* Topic Title */}
              <div style={{ 
                flex: 1, 
                textAlign: 'center', 
                margin: '0 12px',
                overflow: 'hidden'
              }}>
                <Text strong style={{ 
                  fontSize: '14px',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {currentTopic?.topic_title || 'Topic'}
                </Text>
                {progressTopics.length > 1 && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {currentTopicIndex + 1} of {progressTopics.length} topics
                  </Text>
                )}
              </div>

              {/* Right Arrow Button */}
              <Button
                type="text"
                onClick={() => {
                  if (currentTopicIndex < progressTopics.length - 1) {
                    setCurrentTopicIndex(currentTopicIndex + 1);
                  }
                }}
                disabled={!currentTopic || currentTopicIndex === progressTopics.length - 1}
                style={{
                  border: 'none',
                  background: 'white',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: (!currentTopic || currentTopicIndex === progressTopics.length - 1) ? 0.5 : 1
                }}
              >
                <ArrowRightOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />
              </Button>
            </div>

            {/* Learning Path */}
            <div style={{ position: 'relative' }}>
              {/* Connecting Line */}
              <div style={{
                position: 'absolute',
                left: '25px',
                top: '30px',
                bottom: '30px',
                width: '2px',
                background: 'linear-gradient(to bottom, #e6f7ff, #bae7ff)',
                zIndex: 1
              }} />

              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {currentTopic && currentTopic.lessons && currentTopic.lessons.length > 0 ? (
                  currentTopic.lessons.map((progressLesson: LessonProgress, index: number) => {
                    const lessonStatus = {
                      completed: progressLesson.is_completed === 1,
                      isActive: progressLesson.lesson_id.toString() === lessonId,
                      isLocked: false
                    };

                    return (
                      <div key={progressLesson.lesson_id} style={{ position: 'relative', zIndex: 2 }}>
                        <Card
                          hoverable={!lessonStatus.isLocked}
                          onClick={() => !lessonStatus.isLocked && handleLessonClick(progressLesson.lesson_id.toString(), progressLesson.lesson_name)}
                          style={{
                            borderRadius: '12px',
                            border: lessonStatus.isActive ? '2px solid #1890ff' : lessonStatus.completed ? '2px solid transparent' : 'none',
                            background: lessonStatus.isActive 
                              ? '#e6f7ff' 
                              : lessonStatus.completed 
                              ? 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box' 
                              : 'white',
                            boxShadow: lessonStatus.isActive 
                              ? '0 4px 16px rgba(24, 144, 255, 0.3)' 
                              : '0 2px 8px rgba(0,0,0,0.06)',
                            cursor: lessonStatus.isLocked ? 'not-allowed' : 'pointer',
                            opacity: lessonStatus.isLocked ? 0.6 : 1
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px'
                          }}>
                            {/* Lesson Icon/Circle */}
                            <div style={{
                              width: '50px',
                              height: '50px',
                              borderRadius: '50%',
                              background: lessonStatus.completed ?
                                'linear-gradient(135deg, #4096ff 0%, #1890ff 100%)' :
                                lessonStatus.isActive ?
                                'linear-gradient(135deg, #4096ff 0%, #1890ff 100%)' :
                                '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '12px',
                              flexShrink: 0
                            }}>
                              <div style={{
                                width: '25px',
                                height: '25px',
                                borderRadius: '50%',
                                background: lessonStatus.completed || lessonStatus.isActive ? 'white' : '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <div style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  background: lessonStatus.completed ? '#4096ff' : lessonStatus.isActive ? '#4096ff' : '#bfbfbf'
                                }} />
                              </div>
                            </div>
                            
                            {/* Lesson Content */}
                            <div style={{ flex: 1, textAlign: 'left' }}>
                              <Title level={5} style={{ 
                                margin: '0 0 2px 0',
                                color: lessonStatus.isActive ? '#1890ff' : '#262626',
                                fontSize: '14px'
                              }}>
                                {progressLesson.lesson_name}
                              </Title>
                              {lessonStatus.completed && (
                                <Text style={{ 
                                  color: '#52c41a',
                                  fontSize: '12px'
                                }}>
                                  ✓ Completed
                                </Text>
                              )}
                            </div>
                          </div>
                        </Card>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text type="secondary">No lessons available</Text>
                  </div>
                )}
              </Space>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No learning path available</Text>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default LessonViewer;
